import React, { useEffect, useState } from 'react';
import { ScrollView, StyleSheet, TextInput, View } from 'react-native';
import { Text } from '../components/Texte';
import { SafeAreaView } from 'react-native-safe-area-context';
import { FileText, Search } from '../components/icons';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { ScoreRing } from '../components/ScoreRing';
import { CarteScore } from '../components/CarteScore';
import { CriteresScore } from '../components/CriteresScore';
import { ScreenHeader } from '../components/ScreenHeader';
import { GradientButton } from '../components/GradientButton';
import { FicheDePosteChamp } from '../components/FicheDePosteChamp';
import { FeedbackWidget } from '../components/FeedbackWidget';
import { FriseEtapes } from '../components/FriseEtapes';
import { CarteDepotCv } from '../components/CarteDepotCv';
import { SectionRepliable } from '../components/SectionRepliable';
import { PiedAction } from '../components/PiedAction';
import { useGabarit } from '../hooks/useGabarit';
import { cvScoreInputKey } from '../utils/resultRef';
import { confirmer } from '../utils/confirmation';
import { palierScore } from '../utils/palierScore';
import { titreDepuisNom } from '../utils/titreCv';
import { useClaude } from '../hooks/useClaude';
import { lireDocumentImporte, pickDocumentFile, type PickedFile, type PickedImage } from '../services/mediaPicker';
import { useFicheDePoste, type FicheDePosteInitiale } from '../hooks/useFicheDePoste';
import { saveGeneratedCv } from '../services/cv';
import { FEATURE_ACCENTS } from '../theme/featureAccents';
import { colors, formes, layout, radius, screenPadding, spacing, typography } from '../theme/tokens';
import type { CvScore, GeneratedCv } from '../types';
import { t, tBrut } from '../i18n';

// Préremplissage possible depuis « Crée mon CV » (CV généré + offre déjà fournie) → pas de ressaisie.
export interface CvScoreInitialOffer {
  text?: string;
  file?: PickedFile | null;
  images?: PickedImage[];
}
export interface CvImproveSource {
  file?: PickedFile | null;
  text?: string;
  /** Fiche de poste qui a servi à la notation : reprise par « Optimiser mon CV » (1.1.0). */
  fiche?: FicheDePosteInitiale;
}
export interface CvScoreScreenProps {
  onBack?: () => void;
  // Signale à la couche navigation qu'un résultat non persisté est affiché (garde de sortie).
  onResultChange?: (hasResult: boolean) => void;
  initialCvText?: string;
  initialOffer?: CvScoreInitialOffer;
  // CV déjà choisi ailleurs (« Importer mon CV » de Carrière) : préremplit la sélection, sans
  // lancer l'analyse — le décompte de quota doit rester déclenché par la personne.
  initialFile?: PickedFile | null;
  onImprove?: (cv: CvImproveSource) => void;
  // Enregistrement d'un CV importé dans « Mes CV » : la structuration a réussi, l'appelant ouvre
  // l'éditeur sur le CV créé. Absent = l'action n'est pas proposée.
  onSaveImported?: (cvId: string, cv: GeneratedCv) => void;
}

/**
 * Verdict en une phrase, par palier du score ATS. Les paliers suivent les couleurs de l'anneau
 * (rouge sous 60, bleu jusqu'à 85, vert au-delà) pour que le mot et la couleur disent la même chose.
 * C'est NOTRE lecture du score, pas une mesure supplémentaire du serveur.
 */
export function verdictScore(score: number): string {
  return t(`cvScore.verdict.${palierScore(score)}`);
}

const ACCENT = FEATURE_ACCENTS.cv;

export function CvScoreScreen({ onBack, initialCvText, initialOffer, initialFile, onImprove, onSaveImported, onResultChange }: CvScoreScreenProps = {}) {
  const { large } = useGabarit();
  const { scoreCv, extractCv, loading, error } = useClaude();
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [file, setFile] = useState<PickedFile | null>(initialFile ?? null);
  // Plus de champ de saisie : `text` ne porte que le CV prérempli (« Tester ce CV dans Note mon CV »)
  // et alimente onImprove. Non éditable par l'utilisateur.
  const [text] = useState(initialCvText ?? '');
  const [score, setScore] = useState<CvScore | null>(null);

  // La note n'est jamais persistée : quitter l'écran la détruit. On le signale à la navigation,
  // qui pose la confirmation de sortie (bouton ET balayage). Aucune alerte tant qu'il n'y a rien.
  useEffect(() => {
    onResultChange?.(score !== null);
  }, [score, onResultChange]);
  // Clé du résultat (feedback) dérivée de l'ENTRÉE notée, figée quand la note arrive.
  const [scoreKey, setScoreKey] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  // Poste recherché (titre seul, facultatif) : note aussi l'adéquation au poste.
  const [targetRole, setTargetRole] = useState('');

  // Fiche de poste optionnelle (3 modes) : logique partagée avec l'entretien et « Modifier mon CV ».
  const fiche = useFicheDePoste({ initiale: initialOffer, onErreur: setLocalError });

  // Une seule section facultative ouverte à la fois. Une offre déjà fournie (« Crée mon CV ») ouvre
  // la fiche d'emblée : la cacher ferait croire qu'elle a été perdue.
  const [ouvert, setOuvert] = useState<'poste' | 'fiche' | null>(fiche.presente ? 'fiche' : null);
  const bascule = (section: 'poste' | 'fiche') => setOuvert((actuel) => (actuel === section ? null : section));

  const pickCv = async () => {
    setLocalError(null);
    const outcome = await pickDocumentFile();
    if (outcome.canceled) return;
    if (outcome.error) return setLocalError(outcome.error);
    if (outcome.file) setFile(outcome.file);
  };

  const analyze = async () => {
    setLocalError(null);
    let cv: { pdf?: { data: string }; docx?: { data: string }; text?: string } | null = null;
    if (file) {
      const { payload, error } = await lireDocumentImporte(file);
      if (!payload) return setLocalError(error ?? t('cvScore.failed'));
      cv = payload;
    } else if (text.trim()) {
      cv = { text };
    }
    if (!cv) return;

    /**
     * LE `try` COUVRE MAINTENANT `fiche.construire`, qui lit lui aussi un fichier. Sans lui, une fiche
     * de poste illisible interrompait le gestionnaire sans rien afficher : le bouton « Analyser »
     * ne faisait rien, et rien ne disait pourquoi.
     */
    try {
      const offer = await fiche.construire();
      const role = targetRole.trim();
      // Le titre ne fait pas partie de l'entrée notée : cvScoreInputKey l'ignore, la clé du feedback
      // ne change donc pas.
      const cvTitle = file ? titreDepuisNom(file.name) : null;
      const input = {
        ...cv,
        ...(offer ? { offer } : {}),
        ...(role ? { targetRole: role } : {}),
        ...(cvTitle ? { cvTitle } : {}),
      };
      const result = await scoreCv(input);
      if (result) {
        setScore(result);
        setScoreKey(cvScoreInputKey(input));
      }
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : t('cvScore.failed'));
    }
  };

  /**
   * Enregistre le CV noté dans « Mes CV » : transposition IA (cv_import) puis persistance.
   *
   * Le score et les conseils viennent de la NOTATION qui vient d'avoir lieu, pas de la
   * transposition — celle-ci ne note pas et renvoie 0/[]. Le CV enregistré porte donc un score
   * authentique, produit par le mode dont c'est le métier.
   *
   * Quota et erreur : extractCv passe par useClaude, qui ouvre le Paywall sur dépassement. Un
   * échec technique ne coûte rien (l'incrément serveur suit la validation) et ne persiste rien.
   */
  const saveImported = async () => {
    if (!score || !onSaveImported) return;
    setSaveError(null);

    let source: { pdf?: { data: string }; docx?: { data: string }; text?: string } | null = null;
    if (file) {
      const { payload, error } = await lireDocumentImporte(file);
      if (!payload) return setSaveError(error ?? t('cvScore.saveFailed'));
      source = payload;
    } else if (text.trim()) {
      source = { text };
    }
    if (!source) return;

    setSaving(true);
    try {
      const structured = await extractCv(source);
      if (!structured) return; // erreur ou quota : useClaude a déjà pris la main
      const cv: GeneratedCv = { ...structured, ats_score: score.ats_score, advice: score.advice };
      const cvId = await saveGeneratedCv(cv, cv.headline.trim() || tBrut('cvScore.importedTitle'));
      onSaveImported(cvId, cv);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : t('cvScore.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const canAnalyze = file !== null || text.trim().length > 0;
  const shownError = localError ?? error;
  const posteSaisi = targetRole.trim();

  // Le résultat PREND LA PLACE du formulaire : la note arrive en tête d'écran, sans défilement
  // pour la trouver. « Refaire la note » ramène au formulaire avec toutes les saisies conservées
  // (elles vivent dans cet écran, pas dans le formulaire).
  if (score) {
  const rScore = (
    <>
            <CarteScore
              testID="cv-carte"
              anneauTestID="cv-score"
              detailTestID="cv-score-detail"
              score={score.ats_score}
              titre={verdictScore(score.ats_score)}
              detail={t('cvScore.resultDetail', { count: score.advice.length })}
            />

            {/* Absent tant que le serveur ne renvoie pas les quatre critères : jamais de valeur inventée. */}
            {score.criteria ? <CriteresScore criteres={score.criteria} testID="cv-criteres" /> : null}

                </>
  );
  const rMatch = (
    <>
{score.match_score !== undefined ? (
              <View testID="cv-match" style={styles.matchBlock}>
                <View style={styles.ringRow}>
                  <ScoreRing score={score.match_score} testID="cv-match-score" />
                  <Text style={styles.scoreLabel}>{t('cvScore.offerMatch')}</Text>
                </View>
                {score.strengths && score.strengths.length > 0 ? (
                  <>
                    <Text style={styles.sectionLabel}>{t('cvScore.strengths')}</Text>
                    {score.strengths.map((s, index) => (
                      <Card key={index} testID={`cv-match-strength-${index}`}>
                        <Text style={styles.tip}>{s}</Text>
                      </Card>
                    ))}
                  </>
                ) : null}
                {score.gaps && score.gaps.length > 0 ? (
                  <>
                    <Text style={styles.sectionLabel}>{t('cvScore.gaps')}</Text>
                    {score.gaps.map((g, index) => (
                      <Card key={index} testID={`cv-match-gap-${index}`}>
                        <Text style={styles.tip}>{g}</Text>
                      </Card>
                    ))}
                  </>
                ) : null}
              </View>
            ) : null}

                </>
  );
  const rConseils = (
    <>
{/* Les conseils gardent l'ordre du serveur. Aucun gain en points : il n'en mesure pas,
                en afficher serait inventer. */}
            {score.advice.length > 0 ? (
              <View testID="cv-advice" style={styles.conseils}>
                <Text accessibilityRole="header" style={styles.titreSection}>
                  {t('cvScore.advicePrio')}
                </Text>
                {score.advice.map((tip, index) => (
                  <View key={index} testID={`advice-${index}`} style={styles.conseil}>
                    <View testID={`advice-${index}-num`} style={styles.numero}>
                      <Text style={styles.numeroTexte}>{index + 1}</Text>
                    </View>
                    <Text style={styles.conseilTexte}>{tip}</Text>
                  </View>
                ))}
              </View>
            ) : null}

                </>
  );
  const rActions = (
    <>
{/* Proposé APRÈS le score, jamais à l'import : demander d'enregistrer avant que la
                personne sache ce que vaut son CV, ce serait un engagement sans information. */}
            {onSaveImported ? (
              <>
                <Button
                  title={t('cvScore.saveImported')}
                  variant="secondary"
                  onPress={() => void saveImported()}
                  loading={saving}
                  testID="cv-save-imported"
                />
                {/* La vue résultat n'a plus de pied : l'erreur technique du hook (extractCv) s'affiche
                    ici, à côté du bouton qui l'a provoquée, comme l'échec d'enregistrement. */}
                {saveError ?? error ? (
                  <Text testID="cv-save-error" style={styles.error}>
                    {saveError ?? error}
                  </Text>
                ) : null}
              </>
            ) : null}

            <Button
              title={t('cvScore.rescore')}
              variant="secondary"
              onPress={() =>
                // La note n'est pas enregistrée et la refaire repasse par un quota : on confirme.
                confirmer(t('cvScore.rescoreTitre'), t('cvScore.rescoreMessage'), [
                  { text: t('cvScore.rescoreAnnuler'), style: 'cancel' },
                  {
                    text: t('cvScore.rescore'),
                    style: 'destructive',
                    onPress: () => {
                      setSaveError(null);
                      setScore(null);
                    },
                  },
                ])
              }
              testID="cv-rescore"
            />

            <FeedbackWidget key={scoreKey} feature="cv_score" resultKey={scoreKey} testID="cvscore-feedback" />
    </>
  );
  const rPied = (
    <>
        {onImprove ? (
          <PiedAction testID="cv-result-actions" enColonne={large}>
            <GradientButton
              variante="cta"
              hauteur="lg"
              label={t('cvScore.improve')}
              onPress={() =>
                onImprove({
                  file,
                  text,
                  ...(fiche.presente ? { fiche: { text: fiche.texte, file: fiche.fichier, images: fiche.captures } } : {}),
                })
              }
              withArrow
              testID="cv-improve"
            />
          </PiedAction>
        ) : null}
    </>
  );

    return (
      <SafeAreaView style={styles.container} edges={['top']}>
        <ScreenHeader onBack={onBack} title={t('cvScore.atsScore')} variante="carte" testID="cvscore-back" />

        {/* Ordinateur : la note et la suite à donner à gauche, le détail et les conseils à droite. */}
        <ScrollView contentContainerStyle={[styles.content, large && styles.contentLargeSc]}>
          <View testID="cv-result" style={[styles.result, large && styles.resultatLargeSc]}>
            {large ? (
              <>
                <View style={styles.colSc}>
                  {rScore}
                  {rPied}
                </View>
                <View style={styles.colSc}>
                  {rMatch}
                  {rConseils}
                  {rActions}
                </View>
              </>
            ) : (
              <>
                {rScore}
                {rMatch}
                {rConseils}
                {rActions}
              </>
            )}
          </View>
        </ScrollView>

        {large ? null : rPied}
      </SafeAreaView>
    );
  }

  const fGauche = (
    <>
        <FriseEtapes
          testID="cv-frise"
          etapes={[t('cvScore.frise.import'), t('cvScore.frise.evalue'), t('cvScore.frise.score')]}
          etape={canAnalyze ? 1 : 0}
        />

        <CarteDepotCv
          testID="cv-depot"
          boutonTestID="cv-pick"
          sousTitreTestID={file ? 'cv-filename' : undefined}
          pret={canAnalyze}
          titre={canAnalyze ? t('cvScore.dropReady') : t('cvScore.dropTitle')}
          sousTitre={file ? file.name : canAnalyze ? t('cvScore.dropTextReady') : t('cvScore.dropSub')}
          bouton={canAnalyze ? t('cvScore.dropChange') : t('cvScore.dropPick')}
          onPress={pickCv}
        />

            </>
  );
  const fDroite = (
    <>
<Text style={styles.sectionLabel}>{t('cvScore.moreAccurate')}</Text>

        {/* ─── Poste visé (optionnel) ─── */}
        <SectionRepliable
          testID="cv-section-poste"
          titre={t('cvScore.targetRoleLabel')}
          sousTitre={posteSaisi || t('cvScore.optional')}
          icone={<Search size={18} color={colors.electricDeep} strokeWidth={2.2} />}
          indicateur="plus"
          ouverte={ouvert === 'poste'}
          onToggle={() => bascule('poste')}
        >
          <TextInput
            testID="cv-target-role"
            style={styles.roleInput}
            placeholder={t('cvScore.targetRolePlaceholder')}
            placeholderTextColor={colors.grey500}
            value={targetRole}
            onChangeText={setTargetRole}
          />
          <Text style={styles.offerHint}>{t('cvScore.targetRoleHint')}</Text>
        </SectionRepliable>

        {/* ─── Fiche de poste (optionnel) ─── */}
        <SectionRepliable
          testID="cv-section-fiche"
          titre={t('cvScore.offerLabel')}
          sousTitre={fiche.presente ? t('cvScore.ficheAdded') : t('cvScore.optional')}
          icone={<FileText size={18} color={colors.electricDeep} strokeWidth={2.2} />}
          indicateur="plus"
          ouverte={ouvert === 'fiche'}
          onToggle={() => bascule('fiche')}
        >
          <FicheDePosteChamp
            fiche={fiche}
            prefixe="cv-offer"
            titre={t('cvScore.offerLabel')}
            aide={t('cvScore.offerHint')}
            placeholder={t('cvScore.offerPlaceholder')}
            libelleFichier={t('cvScore.offerFile')}
            libelleCaptures={t('cvScore.offerCaptures')}
            sansCadre
          />
        </SectionRepliable>

    </>
  );
  const fPied = (
    <>
      {/* Un échec s'affiche ICI, à côté du bouton qui l'a provoqué, pas plus haut dans la page. */}
      <PiedAction testID="cv-actions" enColonne={large}>
        {shownError ? (
          <Text testID="cv-error" style={styles.error}>
            {shownError}
          </Text>
        ) : (
          <Text testID={canAnalyze ? 'cv-ready' : 'cv-empty'} style={styles.hint}>
            {canAnalyze ? t('cvScore.hintReady') : t('cvScore.hintEmpty')}
          </Text>
        )}
        <GradientButton
          variante="cta"
          hauteur="lg"
          label={t('cvScore.submit')}
          onPress={analyze}
          loading={loading}
          disabled={!canAnalyze}
          withArrow
          testID="cv-submit"
        />
      </PiedAction>
    </>
  );

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <ScreenHeader onBack={onBack} title={t('cvScore.title')} variante="carte" testID="cvscore-back" />

      {/* Ordinateur : le dépôt du CV à gauche, les précisions facultatives et le lancement à droite. */}
      {large ? (
        <ScrollView testID="cv-colonnes" contentContainerStyle={styles.colonnesSc} keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
          <View style={styles.colSc}>{fGauche}</View>
          <View style={styles.colSc}>
            {fDroite}
            <View style={styles.piedSc}>{fPied}</View>
          </View>
        </ScrollView>
      ) : (
      <ScrollView contentContainerStyle={styles.content} keyboardShouldPersistTaps="handled" keyboardDismissMode="on-drag">
        {fGauche}
        {fDroite}
      </ScrollView>
      )}

      {large ? null : fPied}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  colonnesSc: {
    flexGrow: 1,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.xl,
    width: '100%',
    maxWidth: layout.pageMaxWidth,
    alignSelf: 'center',
    padding: spacing.xxl + spacing.xl,
  },
  colSc: { flex: 1, minWidth: 0, gap: spacing.md },
  piedSc: { marginTop: spacing.sm },
  contentLargeSc: { width: '100%', maxWidth: layout.pageMaxWidth, alignSelf: 'center', padding: spacing.xxl + spacing.xl },
  resultatLargeSc: { flexDirection: 'row', alignItems: 'flex-start', gap: spacing.xl },
  container: { flex: 1, backgroundColor: colors.grey50 },
  content: { padding: screenPadding, paddingTop: spacing.sm, gap: spacing.md },

  offerHint: { fontSize: typography.small.size, lineHeight: typography.small.lineHeight, color: colors.grey500 },
  roleInput: {
    minHeight: formes.hauteur.md,
    borderWidth: 1.5,
    borderColor: colors.grey200,
    borderRadius: radius.lg,
    paddingHorizontal: spacing.md,
    fontSize: typography.body.size,
    fontWeight: '500',
    color: colors.night,
    backgroundColor: colors.grey50,
  },

  hint: { textAlign: 'center', fontSize: 12.5, color: colors.grey500 },
  error: { textAlign: 'center', fontSize: typography.small.size, color: colors.error },

  result: { gap: spacing.md, marginTop: spacing.sm },
  matchBlock: { gap: spacing.md, padding: spacing.lg, borderRadius: radius.lg, backgroundColor: colors.greenSoft },
  ringRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.lg },
  scoreLabel: { flex: 1, fontSize: typography.h3.size, fontWeight: typography.h3.weight, color: colors.grey900 },
  sectionLabel: {
    fontSize: typography.caption.size + 1,
    fontWeight: '800',
    letterSpacing: typography.caption.letterSpacing + 0.2,
    textTransform: 'uppercase',
    color: colors.grey500,
  },
  tip: { fontSize: typography.body.size, color: colors.grey700 },

  conseils: { gap: spacing.sm },
  titreSection: { fontSize: typography.h3.size, lineHeight: typography.h3.lineHeight, fontWeight: typography.h3.weight, color: colors.night },
  conseil: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.md,
    padding: spacing.md,
    borderRadius: formes.rayon.md,
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey200,
  },
  numero: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: ACCENT.tint,
  },
  numeroTexte: { fontSize: 13, fontWeight: '800', color: ACCENT.ink },
  // `flex: 1` + `minWidth: 0` : un conseil long passe à la ligne au lieu de sortir de la carte.
  conseilTexte: { flex: 1, minWidth: 0, fontSize: typography.body.size, lineHeight: typography.body.lineHeight, color: colors.grey700 },
});
